// Run generation system
import {
    BossTemplate,
    EnemyTemplate,
    EnemyEffectPoolEntry,
    ElementType,
    getRandomBoss,
    getTemplatesMatchingAspects,
    getBudgetsForThreatLevel,
    getEffectsForThreatLevel,
    getThreatMultipliers,
    BossAction,
} from './EnemyData';

// Types for generated enemies and encounters

export interface GeneratedEnemyAction {
    effectId: string;
    cooldown: number;
    currentCooldown: number; // Turns until next activation
    params: Record<string, unknown>;
}

export interface GeneratedEnemy {
    id: string;
    templateId: string;
    name: string;
    maxHP: number;
    currentHP: number;
    resists: Record<ElementType, number>;
    actions: GeneratedEnemyAction[];
    aspects: ElementType[];
}

export interface GeneratedBoss {
    id: string;
    bossId: string;
    name: string;
    maxHP: number;
    currentHP: number;
    resists: Record<ElementType, number>;
    actions: GeneratedEnemyAction[];
    aspects: ElementType[];
}

export type EncounterType = 'fight' | 'elite' | 'treasure' | 'boss';

export interface Encounter {
    id: string;
    type: EncounterType;
    position: number; // 1-4 for regular, 5 for boss
    path: 'left' | 'right' | 'center'; // center for start/boss
    enemies: GeneratedEnemy[];
    boss?: GeneratedBoss;
}

export interface RunPlan {
    seed: string;
    threatLevel: number;
    boss: BossTemplate;
    aspects: ElementType[];
    encounters: Encounter[];
}

// Simple seeded random number generator
class SeededRandom {
    private seed: number;

    constructor(seed: string) {
        this.seed = this.hashString(seed);
    }

    private hashString(str: string): number {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash);
    }

    next(): number {
        this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
        return this.seed / 0x7fffffff;
    }

    nextInt(min: number, max: number): number {
        return Math.floor(this.next() * (max - min + 1)) + min;
    }

    pick<T>(array: T[]): T {
        return array[Math.floor(this.next() * array.length)];
    }

    shuffle<T>(array: T[]): T[] {
        const result = [...array];
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(this.next() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
    }
}

export function generateRun(threatLevel: number, seed?: string, fixedBoss?: BossTemplate): RunPlan {
    const runSeed = seed || `run_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const rng = new SeededRandom(runSeed);

    // 1. Pick a random boss (or use fixed boss if provided)
    const boss = fixedBoss || getRandomBoss();
    const aspects = boss.aspects;

    // 2. Get budgets for this threat level
    const budgets = getBudgetsForThreatLevel(threatLevel);
    const multipliers = getThreatMultipliers(threatLevel);

    // 3. Get valid enemy templates (matching boss aspects)
    const validTemplates = getTemplatesMatchingAspects(aspects);
    if (validTemplates.length === 0) {
        throw new Error(`No enemy templates match boss aspects: ${aspects.join(', ')}`);
    }

    // 4. Determine which path has treasure (always left = treasure, right = boss)
    const treasurePath: 'left' | 'right' = 'left';
    const regularPath: 'left' | 'right' = 'right';

    // 5. Generate all 7 encounter slots
    const encounters: Encounter[] = [];
    let encounterId = 0;

    // Position 1: Start encounter (1-2 enemies, always center)
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        1,
        'center',
        validTemplates,
        rng.nextInt(1, 2), // Always 1-2 enemies for start
        threatLevel,
        multipliers,
        rng
    ));

    // Position 2: Two path options (left and right)
    const pos2EnemyCount = calculateEnemyCount(budgets.fight, rng);
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        2,
        'left',
        validTemplates,
        pos2EnemyCount,
        threatLevel,
        multipliers,
        rng
    ));
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        2,
        'right',
        validTemplates,
        calculateEnemyCount(budgets.fight, rng),
        threatLevel,
        multipliers,
        rng
    ));

    // Position 3: Two path options (treasure path has elite, other is regular)
    // Elite encounter on treasure path
    const eliteEnemyCount = budgets.elite > 0 ? calculateEnemyCount(budgets.elite, rng) : calculateEnemyCount(budgets.fight, rng);
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        3,
        treasurePath,
        validTemplates,
        eliteEnemyCount,
        threatLevel,
        multipliers,
        rng,
        budgets.elite > 0 ? 'elite' : 'fight'
    ));
    // Regular encounter on other path
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        3,
        regularPath,
        validTemplates,
        calculateEnemyCount(budgets.fight, rng),
        threatLevel,
        multipliers,
        rng
    ));

    // Position 4: One treasure, one regular (locked paths from position 3)
    // Treasure on treasure path
    encounters.push({
        id: `enc_${encounterId++}`,
        type: 'treasure',
        position: 4,
        path: treasurePath,
        enemies: [], // Treasure has no enemies
    });
    // Regular fight on other path
    encounters.push(generateFightEncounter(
        `enc_${encounterId++}`,
        4,
        regularPath,
        validTemplates,
        calculateEnemyCount(budgets.fight, rng),
        threatLevel,
        multipliers,
        rng
    ));

    // Position 5: Boss encounter (center, only reached via boss path)
    encounters.push({
        id: `enc_${encounterId++}`,
        type: 'boss',
        position: 5,
        path: 'center', // Boss is at center after path convergence
        enemies: [],
        boss: undefined, // Boss data is in runPlan.boss, generated at load time
    });

    return {
        seed: runSeed,
        threatLevel,
        boss,
        aspects,
        encounters,
    };
}

function calculateEnemyCount(budget: number, rng: SeededRandom): number {
    // Scale enemy count by budget: 1-2 at low budget, up to 6 at max
    // Budget ranges from ~2 (threat 1) to ~6 (threat 10)
    if (budget <= 2) return rng.nextInt(1, 2);
    if (budget <= 3) return rng.nextInt(1, 3);
    if (budget <= 4) return rng.nextInt(2, 4);
    if (budget <= 5) return rng.nextInt(2, 5);
    return rng.nextInt(3, 6);
}

function generateFightEncounter(
    id: string,
    position: number,
    path: 'left' | 'right' | 'center',
    templates: EnemyTemplate[],
    enemyCount: number,
    threatLevel: number,
    multipliers: ReturnType<typeof getThreatMultipliers>,
    rng: SeededRandom,
    type: EncounterType = 'fight'
): Encounter {
    const enemies: GeneratedEnemy[] = [];

    for (let i = 0; i < enemyCount; i++) {
        const template = rng.pick(templates);
        enemies.push(generateEnemy(template, threatLevel, multipliers, rng, i));
    }

    return {
        id,
        type,
        position,
        path,
        enemies,
    };
}

function generateEnemy(
    template: EnemyTemplate,
    threatLevel: number,
    multipliers: ReturnType<typeof getThreatMultipliers>,
    rng: SeededRandom,
    index: number
): GeneratedEnemy {
    // Calculate HP with threat scaling
    const baseHP = template.baseHP + template.hpPerRow * (threatLevel - 1);
    const maxHP = Math.round(baseHP * multipliers.hpMult);

    // Get available effects for this threat level
    const availableEffects = getEffectsForThreatLevel(threatLevel, ['attack']);

    // Pick 1-2 actions for this enemy
    const actionCount = rng.nextInt(1, 2);
    const actions: GeneratedEnemyAction[] = [];

    // Always try to give at least one attack
    const attackEffects = availableEffects.filter(e => e.tags.includes('attack'));
    if (attackEffects.length > 0) {
        const effect = rng.pick(attackEffects);
        actions.push({
            effectId: effect.effectId,
            cooldown: effect.cooldown,
            currentCooldown: rng.nextInt(1, effect.cooldown), // Randomize initial cooldown
            params: { ...effect.params },
        });
    }

    // Maybe add a second action (debuff/board effect)
    if (actionCount > 1 && rng.next() > 0.5) {
        const supportEffects = getEffectsForThreatLevel(threatLevel, ['debuff', 'board']);
        if (supportEffects.length > 0) {
            const effect = rng.pick(supportEffects);
            actions.push({
                effectId: effect.effectId,
                cooldown: effect.cooldown,
                currentCooldown: rng.nextInt(2, effect.cooldown + 1),
                params: { ...effect.params },
            });
        }
    }

    // Fallback: if no actions, give a basic attack
    if (actions.length === 0) {
        actions.push({
            effectId: 'enem_eff_attack_laser',
            cooldown: template.defaultCooldown,
            currentCooldown: rng.nextInt(1, template.defaultCooldown),
            params: { baseDamage: 14, damageType: template.aspects[0] || 'THERMAL' },
        });
    }

    return {
        id: `enemy_${template.id}_${index}_${rng.next().toString(36).substr(2, 6)}`,
        templateId: template.id,
        name: template.name,
        maxHP,
        currentHP: maxHP,
        resists: { ...template.resists },
        actions,
        aspects: [...template.aspects],
    };
}

// Generate boss encounter (called separately when player reaches boss)
export function generateBossEncounter(
    boss: BossTemplate,
    threatLevel: number
): Encounter {
    const multipliers = getThreatMultipliers(threatLevel);
    const maxHP = Math.round(boss.baseHP * multipliers.hpMult);

    const actions: GeneratedEnemyAction[] = boss.actions.map((action, i) => ({
        effectId: action.effectId,
        cooldown: action.cooldown,
        currentCooldown: i === 0 ? action.cooldown : action.cooldown + i, // Stagger initial cooldowns
        params: { ...action.params },
    }));

    const generatedBoss: GeneratedBoss = {
        id: `boss_${boss.id}`,
        bossId: boss.id,
        name: boss.name,
        maxHP,
        currentHP: maxHP,
        resists: { ...boss.resists },
        actions,
        aspects: [...boss.aspects],
    };

    return {
        id: 'enc_boss',
        type: 'boss',
        position: 5,
        path: 'center',
        enemies: [],
        boss: generatedBoss,
    };
}

// Get the encounter for a specific position and path
export function getEncounterAt(plan: RunPlan, position: number, path: 'left' | 'right' | 'center'): Encounter | undefined {
    return plan.encounters.find(e => e.position === position && e.path === path);
}

// Get the start encounter
export function getStartEncounter(plan: RunPlan): Encounter {
    const enc = plan.encounters.find(e => e.position === 1 && e.path === 'center');
    if (!enc) throw new Error('No start encounter in run plan');
    return enc;
}
